export type EventObject = {
  object_id: number
  description: string
  tags?: string[]
}

export type Event =
  | {
      event_json_version: string
      id: string
      alert: boolean
      confidence: number
      dwell_time: number
      detections: string
      metadata: EventMetadata
      output: {
        stream: {
          id: string
          business_logics: EventBusinessLogic[]
        }
      }
      recordings: EventRecording[]
      tags: Tag[]
      ocr_results: OcrResult[]
      machine_code_results: MachineCodeResult[]
      tracked_people?: TrackedPerson[]
      timestamp: {
        $date: number
      }
      measurement?: Measurement
      description?: string
      objects?: EventObject[]
    }
  | EventLegacy

// NOTE: When modifying this type, ensure to preserve the old version using the EventLegacy type for backward compatibility.
type EventMetadata = {
  device_id: string // deprecated
  stream_id: string // deprecated
  deployment_id: string
  app_id: string
  errors: string[]
  started_at: {
    $date: number
  }
  note: string
  device: DeviceMetadata | null
  camera: CameraMetadata | null
  model_name: string
  group_id?: string
  group_revision?: number
}

type DeviceMetadata = {
  id: string
  name: string
  label: string
}

type CameraMetadata = {
  id: string
  name: string
}

type EventBusinessLogic = {
  [key: string]: {
    [key: string]: {
      label_groups: LabelGroup[]
      rule: string
    }
  }
}

export type Tag = {
  tag_name: string
  tag_source: 'classifier' | 'vp_agent' | 'vp_agent_alert'
}

export enum TrackedPersonState {
  Identified = 'identified',
  Matching = 'matching',
  OneCamera = 'one_camera'
}

export enum TrackedPersonMatch {
  Perfect = 'perfect',
  Face = 'face',
  Appearance = 'appearance'
}

export type TrackedPerson = {
  tracking_id: string
  identity_id?: string | null
  crop_path?: string | null
  state: TrackedPersonState
  match?: TrackedPersonMatch | null
}

export type OcrResult = {
  ocr_string: string
  ocr_confidence: number
  ocr_crop_path: string
  ocr_tag: 'whitelist' | 'blacklist' | null
}

export type MachineCodeResult = {
  machine_code_string: string
  machine_code_type_name: string
  machine_code_crop_path: string
}

// TODO Revisit this type once updated on ML end, it's missing sub labels
type LabelGroup = {
  type: string
  label_id: string
  count_threshold: number
  confidence_threshold: number
  segmentation_threshold: number | null
}

export type EventRecordingMode = 'smart_record' | 'encode'

export type EventVideoCodec = 'h264' | 'h265' | 'mpeg4'

// The recording facts below arrive with event JSON 1.3.0 (group events); older events omit them.
export type EventRecording = {
  video_path: string
  video_bbox_path: string
  thumbnail_path: string
  video_predictions_path: string
  image_path: string
  image_predictions_path: string
  recording_mode?: EventRecordingMode
  video_codec?: EventVideoCodec | null
  video_container?: string | null
  video_width?: number | null
  video_height?: number | null
  video_duration_ms?: number | null
  trigger_offset_ms?: number | null
  pre_event_s?: number
  post_event_s?: number
}

export type EventDateRange = {
  start_date: Date | null
  end_date: Date | null
}

// NOTE: When modifying this type, ensure to preserve the old version using the EventLegacy type for backward compatibility.
type Measurement = {
  id: string
  dimensions: {
    width: number
    height: number
    depth: number
    unit: string
  }
  weight: {
    value: number
    unit: string
  }
}

// Old event format should still be supported for a while
export type EventLegacy = {
  id: string
  alert: boolean
  detections: string
  metadata: EventMetadata
  output: EventLegacyOutput
  recordings: EventLegacyRecording[]
  timestamp: {
    $date: number
  }
  measurement?: Measurement
}

type EventLegacyOutput = {
  stream: {
    id: string
    business_logics: EventLegacyBusinessLogic[]
  }
}

type EventLegacyBusinessLogic = {
  logic_type: string
  parameters: EventLegacyParameter[]
}

type EventLegacyParameter = {
  name: string
  parameter_type: number
  detections: EventLegacyDetection[]
}

type EventLegacyDetection = {
  label_name: string
  count: number
}

type EventLegacyRecording = {
  video_path: string
  thumbnail_path: string
  video_predictions_path: string
  image_path: string
  image_predictions_path: string
}

/*
{
  "_id": {
    "$oid": "671b7b634dfa32f089d135a4"
  },
  "id": "52a0063b-4439-468c-8e20-8900e14636f8",
  "alert": true,
  "confidence": 0.8237637877464294,
  "detections": "ROI->0->area1->person",
  "metadata": {
    "device_id": "50b6f3a0-e129-11ee-88f1-ad24f81b2365",
    "stream_id": "5e2ae3e7-6295-4527-9c7c-4a3919f328f0",
    "deployment_id": "zpMNy3Yq",
    "app_id": "0KDpnBYX",
    "errors": [],
    "started_at": {
      "$date": 1729854264000
    },
    "note": "",
    "camera": {
      "id": "5e2ae3e7-6295-4527-9c7c-4a3919f328f0",
      "name": "Dahua bullet door"
    },
    "device": {
      "id": "50b6f3a0-e129-11ee-88f1-ad24f81b2365",
      "name": "149ad5f52f947d026d53479ca1e9f96a",
      "label": "office jetson"
    },
    "model_name": "People model"
  },
  "output": {
    "stream": {
      "id": "5e2ae3e7-6295-4527-9c7c-4a3919f328f0",
      "business_logics": [
        {
          "logic_type": "2",
          "parameters": [
            {
              "name": "area1",
              "parameter_type": 1,
              "detections": [
                {
                  "label_name": "person",
                  "count": 0
                }
              ]
            }
          ]
        }
      ]
    }
  },
  "recordings": [
    {
      "video_path": "https://storage.googleapis.com/vp_test_data_live/info%40visionplatform.ai/events/52a0063b-4439-468c-8e20-8900e14636f8/videos/52a0063b-4439-468c-8e20-8900e14636f8.mp4?X-Goog-Algorithm=GOOG4-RSA-SHA256&X-Goog-Credential=vp-test-server%40vp-test-environment.iam.gserviceaccount.com%2F20241025%2Fauto%2Fstorage%2Fgoog4_request&X-Goog-Date=20241025T110910Z&X-Goog-Expires=900&X-Goog-SignedHeaders=host&X-Goog-Signature=0708bafc051630f14c8581236c49adbdd381c8494ea25686fe8583c6e8c401304539c5cb6f39ea41b6fcbc5c87cf7a0708e116aa626fa51e7305a8200f6740e520599af86247124404b5a2e8ed45f7979702e7ca5ffecc97f212e1433d34071e0d4172da4778b5145a3856ed7fccc2d5a914e179fcd678335002b8112138140d819c58f9880a53b1734ad16c41f50c8e4148d5c8e97449ea8f544d9a5ac70c8028303dbe46b574e486466a36af4801fa83b66c6438d3d2809cb3cad3bcb175d757b51e6d46d0949c2c251ecc8c73e7da3a5eba4329a8cfe247a3c3e618063bb5bbde7d0e2a330fc31855295afb726d2849c38f51183a6dccc89782a81f42f173",
      "thumbnail_path": "52a0063b-4439-468c-8e20-8900e14636f8/thumbnail/52a0063b-4439-468c-8e20-8900e14636f8_thumbnail.jpg",
      "video_predictions_path": "",
      "image_path": "",
      "image_predictions_path": ""
    }
  ],
  "timestamp": {
    "$date": 1729854306000
  }
}
*/

export type EventFilterTag = {
  tag_name: string
  tag_source: string
}

export type EventListFilters = {
  roi_names: string[]
  line_names: string[]
  detections: string[]
  tags: EventFilterTag[]
}
